import React from 'react'
import strings from 'constants/strings'
import { FieldError, FieldValues, NestDataObject } from 'react-hook-form'

export const PROFILE_IMAGE_PLACEHOLDER = 'https://sales-media.checkout-apps.com/default/images/storee_avatar.png'

export const MAX_TITLE_LENGTH = 20
export const MAX_TITLE_AI_LENGTH = 60
export const MAX_BODY_LENGTH = 360
export const MAX_BODY_LINES = 7
export const MAX_BTN_LENGTH = 15
export const MAX_BTN_AI_LENGTH = 20
export const MAX_PUSH_TITLE_LENGTH = 30
export const MAX_PUSH_BODY_LENGTH = 100

export const validateImageUrl = (url: string) => {
  if (!url) return true

  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname.toLowerCase()

    const validExtensions = strings.SERVER_IMAGE_FORMATS.split(',')
    const hasValidExtension = validExtensions.some((ext) => pathname.endsWith(ext))

    if (!hasValidExtension) {
      return 'URL must contain a image file with the extension .jpeg, .jpg or .png'
    }

    return true
  } catch {
    return 'Enter a valid URL'
  }
}

export const validateVideoUrl = (url: string) => {
  if (!url) return true

  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname.toLowerCase()

    const validExtensions = strings.SERVER_VIDEO_FORMATS.split(',')
    const hasValidExtension = validExtensions.some((ext) => pathname.endsWith(ext))

    if (!hasValidExtension) {
      return 'URL must contain a video file with the extension .m4v, .mp4 or .mov'
    }

    return true
  } catch {
    return 'Enter a valid URL'
  }
}

// Text limitation utilities
export interface TextLimitationConfig {
  maxLines?: number
  maxCharacters?: number
  lineErrorMessage?: string
  characterErrorMessage?: string
}

export const createTextLimitationHandlers = (config: TextLimitationConfig) => {
  const {
    maxLines,
    maxCharacters,
    lineErrorMessage = 'Text is too long',
    characterErrorMessage = 'Max characters exceeded',
  } = config

  const handlePaste = (e: React.ClipboardEvent<any>) => {
    if (!maxLines && !maxCharacters) return

    e.preventDefault()

    const target = e.target as HTMLTextAreaElement
    const pastedText = e.clipboardData.getData('text')
    const currentValue = target.value
    const selectionStart = target.selectionStart || 0
    const selectionEnd = target.selectionEnd || 0

    // Create new value after paste
    const newValue = currentValue.slice(0, selectionStart) + pastedText + currentValue.slice(selectionEnd)

    // Check constraints
    const lines = newValue.split('\n')
    let finalText = newValue

    // Limit lines if specified
    if (maxLines && lines.length > maxLines) {
      finalText = lines.slice(0, maxLines).join('\n')
    }

    // Limit characters if specified
    if (maxCharacters && finalText.length > maxCharacters) {
      finalText = finalText.slice(0, maxCharacters)
    }

    // Set the trimmed value
    target.value = finalText

    // Set cursor position at the end of pasted text
    const newCursorPosition = Math.min(selectionStart + pastedText.length, finalText.length)
    target.setSelectionRange(newCursorPosition, newCursorPosition)

    // Trigger input event to update react-hook-form
    const inputEvent = new Event('input', { bubbles: true })
    target.dispatchEvent(inputEvent)
  }

  const validateLines = (value: string) => {
    if (!maxLines || !value) return true
    const lines = value.split('\n')
    if (lines.length > maxLines) {
      return lineErrorMessage
    }
    return true
  }

  const getValidationRules = () => {
    const rules: any = {}

    if (maxCharacters) {
      rules.maxLength = {
        value: maxCharacters,
        message: characterErrorMessage,
      }
    }

    if (maxLines) {
      rules.validate = {
        lines: validateLines,
      }
    }

    return rules
  }

  return {
    handlePaste,
    validateLines,
    getValidationRules,
  }
}

export const getNestedError = (errors: NestDataObject<FieldValues, FieldError>, path: string) => {
  return path.split('.').reduce((obj, key) => obj?.[key], errors)
}
